import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BsPlusLg } from "react-icons/bs";
import { HiOutlineMinus } from "react-icons/hi2";
import useForm from "../../../hooks/useForm";
import { checkWriterExists, fetchUserOrderByRoomId, setFormDataGlobState, setPendingPaymentOrderId } from "../../../store";
import {
  ACADEMIC_LEVELS,
  DEADLINES,
  SPACINGS,
  TYPE_OF_ENGLISH,
  TYPE_OF_PAPERS,
  WRITING_TYPES,
} from "../../../Data/orderProperties";
import { USER_INPUT_FIELD_TYPES } from "../../../constants/userInputFieldNames";
import ProgressBar from "../../../components/Form/ProgressBar";
import DropDown from "../../../components/Form/Dropdown";
import Checkbox from "../../../components/Form/Checkbox";
import CustomButton from "../../../components/Form/ArrowButton";
import { ClassFilled } from "@fluentui/react-icons";
import { calculateCost } from "../../../utils/costCalculator";
import showToast from "../../../utils/toastNotifications";
import { ADITIONAL_SERVICE_LABELS, ADITIONAL_SERVICES, SPACING_OPTIONS, tooltips, TYPE_OF_ENGLISH_INDETIFIERS, WORDS_PER_PAGE, WRITING_TYPES_IDENTIFIERS } from "../../../constants/commonConstants";
import { calculateLocalDeadline, calculateLocalDeadlineOptionLabel, currencyFormatter, formatSlideCount } from "../../../utils/formatting";
import FormInput from "../../../components/Form/FormInput";
import { useThunk } from "../../../hooks/useThunk";
import { errorMessages } from "../../../constants/frontendErrorMessages";
import IconButton from "../../../components/Form/IconButton";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { Icon } from "@iconify/react";
import AditionalServiceCard from "../../../components/Form/AditionalServiceCard";
import { AditionalServiceCostCalculator } from "../../../utils/aditionalServiceCostCalculator";
import { getCustomerDiscountName, getDiscountDisplayName, seasonalDiscount } from "../../../utils/seasonalDiscount";
import { normalizeAdditionalServices } from "../../../utils/additionalServices";
import OrderSummarySidebar from "../../../components/Dashboard/OrderSummarySidebar";
import { coursesEnabled } from "../../../config/courseFeatures";
import OrderInclusionsStrip from "../../../components/Form/OrderInclusionsStrip";

const PAPER_TYPE_CATEGORIES = {
  VISUAL: "Visual & presentation tasks",
  WRITING: "Writing tasks",
};

const PAPER_TYPE_SEARCH_ALIASES = {
  Powerpoint: ["power point", "ppt", "pptx", "slides", "presentation"],
  Infographic: ["info graphic", "visual", "poster", "chart", "graphic"],
};

const getPaperTypeDropdownOptions = () => {
  const visualPaperTypes = ["Powerpoint", "Infographic"];
  const visualOptions = visualPaperTypes
    .map((paperName) => TYPE_OF_PAPERS.find((paper) => paper.name === paperName))
    .filter(Boolean)
    .map((paper) => ({
      ...paper,
      category: PAPER_TYPE_CATEGORIES.VISUAL,
      searchAliases: PAPER_TYPE_SEARCH_ALIASES[paper.name] || [],
    }));
  const writingOptions = TYPE_OF_PAPERS
    .filter((paper) => !visualPaperTypes.includes(paper.name))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((paper) => ({
      ...paper,
      category: PAPER_TYPE_CATEGORIES.WRITING,
    }));

  return [...visualOptions, ...writingOptions];
};

const paperTypeDropdownOptions = getPaperTypeDropdownOptions();

const getDeadlineDropdownOptions = () =>
  DEADLINES.map((deadline) => ({
    ...deadline,
    displayName: `${deadline.name} (${calculateLocalDeadlineOptionLabel(deadline.name)})`,
  }));

const Calculation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const createOrder = useSelector((state) => state.createOrder);
  const deadlineDropdownOptions = getDeadlineDropdownOptions();

  const getComparableCalculationData = (data = {}) => ({
    writingType: data.writingType || "",
    typeOfPaper: data.typeOfPaper || "",
    academicLevel: data.academicLevel || "",
    typeOfEnglish: data.typeOfEnglish || "",
    deadline: data.deadline || "",
    spacing: data.spacing || "",
    previousOrderId: data.previousOrderId || "",
    requestedWriterId: data.requestedWriterId || "",
    aditionalServices: normalizeAdditionalServices(data.aditionalServices || []),
    numberOfFigures: data.numberOfFigures || "",
    needPresentation: Boolean(data.needPresentation),
    needDocumentation: Boolean(data.needDocumentation),
    budgetIncrement: data.budgetIncrement || 0,
    slides: data.slides || 0,
    pages: data.pages || 0,
  });

  // Thunks
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    setErrors 
  } = useForm(
    {
      writingType: WRITING_TYPES_IDENTIFIERS.WRITING_FROM_SCRATCH,
      typeOfPaper: "Essay (Any type)",
      academicLevel: "",
      typeOfEnglish: TYPE_OF_ENGLISH_INDETIFIERS.US_ENGLISH,
      deadline: "",
      spacing: SPACING_OPTIONS.DOUBLE_SPACING.label,
      previousOrderId: "",
      requestedWriterId: "",
      aditionalServices: normalizeAdditionalServices(
        ADITIONAL_SERVICES
          .filter(service => ["Turnitin AI Report", "Turnitin Similarity Report"].includes(service.label))
          .map(service => ({
            label: service.label,
            costType: service.costType
          }))
      ),
      numberOfFigures: "",
      needPresentation: false,
      needDocumentation: true, // true since "Essay (Any type)" default selected
      budgetIncrement: 0
    },
    {
      writingType: true,
      typeOfPaper: true,
      typeOfEnglish: true,
      academicLevel: true,
      deadline: true,
      spacing: true,
      previousOrderId: true,
      requestedWriterId: false,
      numberOfFigures: false,
      aditionalServices: false,
      needPresentation: false,
      needDocumentation: false,
      budgetIncrement: true,
    },
    {
      writingType: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      typeOfPaper: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      academicLevel: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      typeOfEnglish: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      deadline: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      spacing: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      previousOrderId: USER_INPUT_FIELD_TYPES.ORDER_ID,
      aditionalServices: USER_INPUT_FIELD_TYPES.ADITIONAL_SERVICES,
      requestedWriterId: USER_INPUT_FIELD_TYPES.WRITER_ID,
      numberOfFigures: USER_INPUT_FIELD_TYPES.NUMBER_FIELD,
      needPresentation: USER_INPUT_FIELD_TYPES.BOOLEAN_FIELD,
      needDocumentation: USER_INPUT_FIELD_TYPES.BOOLEAN_FIELD,
      budgetIncrement: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  // States
  const [count, setCount] = useState(1);
  const [slidescount, setSlidesCount] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isContinuationOrder, setIsContinuationOrder] = useState(false);
  const [doFetchOrderbyId, isLoading, fetchOrderError] = useThunk(fetchUserOrderByRoomId);
  const [doFetchWriterbyId, isLoadingWriter, fetchWriterError] = useThunk(checkWriterExists);

  const handleTypeOfPaperSelect = (value, markTouched = false) => {
    const selectedPaper = TYPE_OF_PAPERS.find((paper) => paper.name === value);

    if (!selectedPaper) {
      return;
    }

    const isPowerpoint = selectedPaper.name === "Powerpoint";

    setErrors((prev) => ({
      ...prev,
      typeOfPaper: "",
    }));
    setFormData((prev) => ({
      ...prev,
      typeOfPaper: selectedPaper.name,
      needDocumentation: !isPowerpoint,
      needPresentation: isPowerpoint,
    }));

    if (markTouched) {
      setTouched({
        ...touched,
        typeOfPaper: true,
      });
    }
  };

  const normalizeNumberOfFigures = (value) => {
    if (value === "") return "";

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "";

    return Math.min(Math.max(Math.floor(numericValue), 1), 50).toString();
  };

  const validateNumberOfFigures = (value) => {
    if (!value) return "Please Provide the number of Figures/Charts";

    const numericValue = Number(value);
    if (!Number.isInteger(numericValue) || numericValue < 1 || numericValue > 50) {
      return "Figures/Charts must be between 1 and 50";
    }

    return "";
  };

  const handleServiceInputChange = (name, value) => {
    if (name !== "numberOfFigures") {
      handleInputChange(name, value);
      return;
    }

    const normalizedValue = normalizeNumberOfFigures(value);
    setFormData((prev) => ({
      ...prev,
      numberOfFigures: normalizedValue,
    }));
    setErrors((prev) => ({
      ...prev,
      numberOfFigures: normalizedValue ? "" : validateNumberOfFigures(normalizedValue),
    }));
  };

  const handleServiceInputBlur = (name, value) => {
    if (name !== "numberOfFigures") {
      handleBlur(name, value);
      return;
    }

    const normalizedValue = normalizeNumberOfFigures(value);
    setFormData((prev) => ({
      ...prev,
      numberOfFigures: normalizedValue,
    }));
    setTouched({
      ...touched,
      numberOfFigures: true,
    });
    setErrors((prev) => ({
      ...prev,
      numberOfFigures: validateNumberOfFigures(normalizedValue),
    }));
  };

  // Progressbar functions
  const steps = [
    { fullLabel: "Calculation", shortLabel: "Calculation" },
    { fullLabel: "Paper Instructions", shortLabel: "Instructions" },
    { fullLabel: "Additional Services", shortLabel: "Services" },
    { fullLabel: "Secure Payment", shortLabel: "Payment" },
  ];

  // Quantity Button Functions
  const increment = () => setCount((count) => totalPages < 100 ? count + 1 : count);
  const decrement = () => setCount(count > 1 ? count - 1 : 1);
  const incrementSlidesCount = () => {
    setSlidesCount((prev) => totalPages < 100 ? prev + 1 : prev);
  };
  const decrementSlidesCount = () => {
    setSlidesCount((prev) => (prev > 1 ? prev - 1 : 1));
  };

  // CheckBox Functions
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleContinuationOrderChange = (e) => {
    const { checked } = e.target;
    setIsContinuationOrder(checked);

    if (!checked) {
      setFormData((prev) => ({ ...prev, previousOrderId: "" }));
      setErrors((prev) => ({ ...prev, previousOrderId: "" }));
    }
  };

  const handleAditionalServices = (service) => {
    if(!service) return;
    setFormData((prev) => {
      const exists = prev.aditionalServices.find((s) => s.label === service.label);
      if (exists) {
        const updated = { ...prev };

        if (service.inputName) {
          updated[service.inputName] = "";
        }
        setErrors((prev) => ({...prev, requestedWriterId: "", numberOfFigures: ""}));
        return {
          ...updated, 
          aditionalServices: prev.aditionalServices.filter((s) => s.label !== service.label)
        }; 
      } else {
        if (service?.isAnabledInput) {
          return {
            ...prev, 
            aditionalServices: [
              ...prev.aditionalServices,
              { label: service.label, costType: service.costType }
            ]
          };
        }
        return {
          ...prev, 
          aditionalServices: [
            ...prev.aditionalServices,
            { label: service.label, costType: service.costType }
          ]
        };
      }
    });
  };

  const isAditionalServiceSelected = (label) => {
    return formData.aditionalServices.some((service) => service.label === label);
  }

  const calculateIndividualServiceCost = (service) => {
    
    const { orderCost, serviceBaseOrderCost, costPerPage } = calculateCost({
      academicLevel: formData.academicLevel,
      deadline: formData.deadline,
      spacing: formData.spacing,
      pages: totalPages, //count + slidescount,
      typeOfPaper: formData.typeOfPaper,
      budgetIncrement: formData.budgetIncrement,
      writingType: formData.writingType,
      typeOfEnglish: formData.typeOfEnglish,
      aditionalServices: formData.aditionalServices,
      returnDetails: true
    })

    if (service.label === ADITIONAL_SERVICE_LABELS.FIGURES) {
      service = {
        ...service,
        perPageMultiplier: formData.numberOfFigures || 0, 
      };
    }
    const cost = Number(
      AditionalServiceCostCalculator(
        service, 
        serviceBaseOrderCost ?? orderCost,
        costPerPage,
        totalPages
      )
    ).toFixed(2) || "00.00"

    return currencyFormatter.format(cost)

  } 

  const referralExtraPercent = createOrder?.referralDiscount?.eligible
    ? Number(createOrder.referralDiscount.referralPercent || 0)
    : 0;
  const loyaltyExtraPercent = !createOrder?.referralDiscount?.eligible && createOrder?.loyaltyProgress?.enabled
    ? Math.max(0, Number(createOrder?.loyaltyProgress?.currentLevel?.discountPercent || 30) - 30)
    : 0;
  const baseDiscountName = getCustomerDiscountName(createOrder?.loyaltyProgress);
  const priceDetails = calculateCost({
    academicLevel: formData.academicLevel,
    deadline: formData.deadline,
    spacing: formData.spacing,
    pages: totalPages,
    typeOfPaper: formData.typeOfPaper,
    budgetIncrement: formData.budgetIncrement,
    writingType: formData.writingType,
    typeOfEnglish: formData.typeOfEnglish,
    aditionalServices: formData.aditionalServices,
    returnDetails: true,
    additionalDiscountPercent: referralExtraPercent || loyaltyExtraPercent,
    additionalDiscountName: referralExtraPercent ? "New customer referral discount" : baseDiscountName,
    additionalDiscountType: referralExtraPercent ? "referral" : "loyalty",
    baseDiscountName,
  });
  const summaryPages = formData.typeOfPaper === "Powerpoint"
    ? (formData.needDocumentation ? count || 1 : 0)
    : (formData.needDocumentation ? count || 1 : 0);
  const summarySlides = formData.typeOfPaper === "Powerpoint"
    ? (formData.needPresentation ? slidescount || 1 : 0)
    : (count > 1 && formData.needPresentation ? slidescount || 1 : 0);
  const summaryTotalUnits = summaryPages + summarySlides;

  // check if previous order ID exists
 useEffect(() => {
  const { previousOrderId } = formData;

  const validateAndFetchOrder = async () => {
    // If input is empty, clear error
    if (!previousOrderId) {
      setErrors((prev) => ({
        ...prev,
        previousOrderId: "",
      }));
      return;
    }

    // If ID format is invalid
    if (!previousOrderId.startsWith("ID") || previousOrderId.length < 6) {
      setErrors((prev) => ({
        ...prev,
        previousOrderId: errorMessages.INVALID_ORDER_ID,
      }));
      return;
    }

    // Valid input - fetch order
    const order = await doFetchOrderbyId({ roomId: previousOrderId });

    if (order?.error) {
      setErrors((prev) => ({
        ...prev,
        previousOrderId: order.error.message,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        previousOrderId: order.response.data?.id,
      }));
      setErrors((prev) => ({
        ...prev,
        previousOrderId: "",
      }));
    }
  };

  validateAndFetchOrder();
}, [formData.previousOrderId]);


  // update service field to calculate cost with multiplier (used only when figures/chart selected)
  useEffect(() => {
    const updatedServices = formData.aditionalServices.map(service => {
      if (service.label === ADITIONAL_SERVICE_LABELS.FIGURES) {
        return {
          ...service,
          perPageMultiplier: formData.numberOfFigures || 0, 
        };
      }
      return service; 
    });

    setFormData(prev => ({
      ...prev,
      aditionalServices: updatedServices
    }));
  },[formData.numberOfFigures])

  //Next Button Function
  const handleNextClick = () => {
    // Mark all fields as touched
    setTouched({
      writingType: true,
      typeOfPaper: true,
      academicLevel: true,
      typeOfEnglish: true,
      deadline: true,
      spacing: true,
      previousOrderId: true,
      requestedWriterId: true,
      aditionalServices: true,
      needPresentation: true,
      needDocumentation: true,
      budgetIncrement: true,
    });

    console.log("validate", validateForm());
    
    if (validateForm()) {
      let slides = 0;
      let pages = 0;

      if (formData.typeOfPaper === "Powerpoint") {
        if (formData.needPresentation) {
          slides = slidescount || 1;
        }
        if (formData.needDocumentation) {
          pages = count || 1;
        }
      } else {
        pages = formData.needDocumentation ? count || 1 : 0;
        slides = count > 1 && formData.needPresentation ? slidescount || 1 : 0;
      }

      const nextFormData = {
        ...formData,
        aditionalServices: normalizeAdditionalServices(formData.aditionalServices),
        slides,
        pages,
      };
      const pendingOrderDraftChanged =
        JSON.stringify(getComparableCalculationData(createOrder.formData)) !==
        JSON.stringify(getComparableCalculationData(nextFormData));

      if (createOrder.pendingPaymentOrderId && pendingOrderDraftChanged) {
        dispatch(setPendingPaymentOrderId(""));
      }

      dispatch(setFormDataGlobState(nextFormData));

      console.log("form data : ", formData)
      navigate("/paper-instructions");
    }
  };

  useEffect(() => {
    if (
      createOrder &&
      createOrder.formData &&
      Object.keys(createOrder.formData)?.length > 0
    ) {
      setFormData({
        ...formData,
        writingType: createOrder.formData?.writingType === "Remove AI Content (Humanize)"
          ? WRITING_TYPES_IDENTIFIERS.REMOVE_AI_CONTENT
          : createOrder.formData?.writingType || WRITING_TYPES_IDENTIFIERS.WRITING_FROM_SCRATCH,
        typeOfPaper: createOrder.formData?.typeOfPaper,
        academicLevel: createOrder.formData?.academicLevel,
        typeOfEnglish: createOrder.formData?.typeOfEnglish,
        deadline: createOrder.formData?.deadline,
        spacing: createOrder.formData?.spacing,
        previousOrderId: createOrder.formData?.previousOrderId,
        requestedWriterId: createOrder.formData?.requestedWriterId,
        aditionalServices: normalizeAdditionalServices(createOrder.formData?.aditionalServices),
        numberOfFigures: createOrder.formData?.numberOfFigures,
        needPresentation: createOrder.formData?.needPresentation,
        needDocumentation: createOrder.formData?.needDocumentation,
        budgetIncrement: createOrder.formData?.budgetIncrement,
      });
      setCount(createOrder.formData.pages);
      setSlidesCount(createOrder.formData.slides);
    }
  }, [createOrder]);

  useEffect(() => {
    if (formData.previousOrderId) {
      setIsContinuationOrder(true);
    }
  }, [formData.previousOrderId]);

  useEffect(() => {
    if (formData.typeOfPaper !== "Powerpoint" && count < 2 && formData.needPresentation) {
      setFormData((prev) => ({
        ...prev,
        needPresentation: false,
      }));
    }
  }, [count, formData.typeOfPaper, formData.needPresentation, setFormData]);

  //set total number of pages according to input parameters
  useEffect(() => {
    let totalPages;
    if (formData.typeOfPaper === "Powerpoint") {
      if (formData.needDocumentation) {
        totalPages = count + slidescount
      } else {
        totalPages = slidescount
      }
    } else {
      if (count > 1 && formData.needPresentation) {
        totalPages = count + slidescount
      } else {
        totalPages = count
      }
    }
    if (totalPages >= 100) {
      showToast("error", "Maximum limit of pages and slides count is reached");
    }
    setTotalPages(totalPages)
  },
    [
      count,
      slidescount,
      formData.typeOfPaper,
      formData.needDocumentation,
      formData.needPresentation
    ])

  console.log("createOrder", formData);

  const getDaysUntilDeadline = (deadline) => {

    if (!deadline) return 0;

    const lower = deadline.toLowerCase().trim();

    if (lower.includes("hour")) {
      const hours = parseFloat(lower);
      return hours / 24;
    }

    if (lower.includes("day")) {
      return parseFloat(lower);
    }

    return 0; 
  };

  return (
    <div className="flex flex-col flex-grow h-screen min-w-[340px] overflow-x-hidden">
      <div className="text-primary font-roboto font-semibold text-[24px] pb-4 ml-[24px] xl:mt-[24px] mt-[104px]">
        <h2 className=" mr-2">Step One (Calculation)</h2>
      </div>
      <div className="flex flex-col flex-grow overflow-y-auto">
        <div className="bg-white  mt-[32px] ml-6 xl:ml-[48px] mr-[24px] lg:mr-[48px] mb-[48px] border border-medium-gray rounded-lg py-8 px-[16px] lg:px-[48px] xl:px-[136px]   md:py-10  max-w-full md:max-w-[2200px] w-auto mx-auto">
          <ProgressBar steps={steps} activeStep={1} completedStep={0} />{" "}
          {coursesEnabled && <p className="mt-2 mb-0 text-center font-inter text-[12px] font-normal leading-4 text-gray">
            Need ongoing help or unsure of the scope?{" "}
            <a href="/courses/new" target="_blank" rel="noopener noreferrer" aria-label="Get a free quote (opens in a new tab)" className="whitespace-nowrap font-medium text-blue-shades underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2">Get a free quote</a>
          </p>}
          <div className="grid w-full grid-cols-1 items-start gap-6 1600-br:grid-cols-[minmax(0,1fr)_280px]">
          <div className="flex flex-col justify-center items-center ">
            {/* Grid Layout for Dropdowns and Pages Section */}
            <div
              className={`grid
            grid-cols-1 
            sm:grid-cols-2 
            md:grid-cols-2 
            lg:grid-cols-2 
            xl:grid-cols-2 
            2xl:grid-cols-2 
            gap-x-6 ${coursesEnabled ? "mt-4" : "mt-10"} w-full px-0
            `}
            >
              {/* First Row: Two Dropdowns */}
              <div>
                <DropDown
                  label="Writing Type"
                  id="wrtingType"
                  name="writingType"
                  value={formData.writingType}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.writingType && errors.writingType}
                  defaultOption="Select the Writing Type"
                  idKey="id"
                  valueKey="name"
                  options={WRITING_TYPES}
                  dropdownDivStyle="w-full sm:w-full lg:w-full"
                  tooltip={tooltips.WRITING_TYPES}
                // error={selectedValue === "" ? "Please select an option" : ""}
                />
              </div>
              <div>
                <DropDown
                  label="Type of Paper"
                  id="typeOfPaper"
                  name="typeOfPaper"
                  value={formData.typeOfPaper}
                  onChange={(name, value) => handleTypeOfPaperSelect(value)}
                  onBlur={handleBlur}
                  error={touched.typeOfPaper && errors.typeOfPaper}
                  defaultOption="Select the type of Paper"
                  idKey="id"
                  valueKey="name"
                  options={paperTypeDropdownOptions}
                  dropdownDivStyle="w-full sm:w-full lg:w-full"
                  tooltip={tooltips.PAPER_TYPE}
                  searchable={true}
                  searchPlaceholder="Search paper type..."
                  fallbackSearchValue="Essay (Any type)"
                // error={selectedValue === "" ? "Please select an option" : ""}
                />
              </div>
              {/* Second Row: Two Dropdowns */}
              <div>
                <DropDown
                  label="Academic Level"
                  id="academicLevel"
                  name="academicLevel"
                  value={formData.academicLevel}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.academicLevel && errors.academicLevel}
                  defaultOption="Select the Academic Level"
                  idKey="id"
                  valueKey="name"
                  options={ACADEMIC_LEVELS}
                  dropdownDivStyle="w-full sm:w-full lg:w-full"
                  tooltip={tooltips.ACADEMIC_LEVEL}

                  // error={selectedValue === "" ? "Please select an option" : ""}
                />
              </div>
              <div>
                <div className="mb-6">
                  <div
                    className="form-label block font-inter text-secondary text-level-3 font-medium leading-[24px] flex items-center gap-1"
                  >
                    Select English Style <span className="text-light-red">*</span>
                    <div className="relative group cursor-pointer">
                      <Icon
                        icon="mdi:help-circle-outline"
                        className="text-[#6B7280]"
                        width={18}
                        height={18}
                      />
                      <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 min-w-[300px] break-words text-center whitespace-normal">
                        {tooltips.ENGLISH_TYPE}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 grid min-h-[44px] grid-cols-3 overflow-hidden rounded-lg border border-[#b9c8ee] bg-white" role="radiogroup" aria-label="Select English Style">
                    {TYPE_OF_ENGLISH.map((option, index) => {
                      const isActive = formData.typeOfEnglish === option.name;

                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            handleInputChange("typeOfEnglish", option.name);
                            handleBlur("typeOfEnglish", option.name);
                          }}
                          className={`flex min-w-0 items-center justify-center gap-1 px-1.5 py-2 text-center text-[11px] font-semibold leading-4 transition-colors duration-150 focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0020BD] sm:px-2 sm:text-[12px] ${index > 0 ? "border-l border-[#b9c8ee]" : ""} ${
                            isActive
                              ? "bg-[#060073] text-white shadow-inner"
                              : "bg-white text-[#24345f] hover:bg-[#eef4ff]"
                          }`}
                          role="radio"
                          aria-checked={isActive}
                        >
                          {isActive && <Icon icon="mdi:check" width={14} height={14} className="shrink-0" aria-hidden="true" />}
                          <span>{option.name}{option.extraAdder ? ` ${option.extraAdder}` : ""}</span>
                        </button>
                      );
                    })}
                  </div>
                  {touched.typeOfEnglish && errors.typeOfEnglish && (
                    <p className="mt-2 text-xs font-medium text-red-400">
                      {errors.typeOfEnglish}
                    </p>
                  )}
                </div>
              </div>
              {/* Second Row: Two Dropdowns */}
              <div className="relative mb-3 rounded-[14px] bg-white [&>div]:mb-0">
                <DropDown
                  label="Deadline"
                  id="deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.deadline && errors.deadline}
                  defaultOption="Select the Deadline"
                  idKey="id"
                  valueKey="name"
                  displayKey="displayName"
                  options={deadlineDropdownOptions}
                  dropdownDivStyle="w-full sm:w-full lg:w-full"
                  dropdownStyle="!text-xs sm:!text-[13px]"
                  optionStyle="text-xs leading-5 sm:text-[13px]"
                  tooltip={tooltips.DEADLINE}

                // error={selectedValue === "" ? "Please select an option" : ""}
                />
                {formData.deadline && formData.deadline?.length > 0 &&
                  <div className="border-x border-b border-[#d9e2f3] bg-[#f5f8fc] px-3 py-2 text-[13px] font-medium leading-5 text-[#475569]">
                    Your order will be delivered by{" "}
                    <span className="font-semibold text-[#1e293b]">
                      {calculateLocalDeadline(formData.deadline) || ""}
                    </span>
                    <span className="text-[12px] font-normal text-[#64748b]">, your local time</span>
                  </div>
                }
              </div>
              <div>
                <div className="mb-6">
                  <div
                    className="form-label block font-inter text-secondary text-level-3 font-medium leading-[24px] flex items-center gap-1"
                  >
                    Select Spacing <span className="text-light-red">*</span>
                    <div className="relative group cursor-pointer">
                      <Icon
                        icon="mdi:help-circle-outline"
                        className="text-[#6B7280]"
                        width={18}
                        height={18}
                      />
                      <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 min-w-[300px] break-words text-center whitespace-normal">
                        {tooltips.SPACING}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 grid min-h-[44px] grid-cols-3 overflow-hidden rounded-lg border border-[#b9c8ee] bg-white" role="radiogroup" aria-label="Select Spacing">
                    {SPACINGS.map((option, index) => {
                      const isActive = formData.spacing === option.name;

                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            handleInputChange("spacing", option.name);
                            handleBlur("spacing", option.name);
                          }}
                          className={`flex min-w-0 items-center justify-center gap-1 px-2 py-2 text-center text-[11px] font-semibold leading-4 transition-colors duration-150 focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0020BD] sm:text-[12px] ${index > 0 ? "border-l border-[#b9c8ee]" : ""} ${
                            isActive
                              ? "bg-[#060073] text-white shadow-inner"
                              : "bg-white text-[#24345f] hover:bg-[#eef4ff]"
                          }`}
                          role="radio"
                          aria-checked={isActive}
                        >
                          {isActive && <Icon icon="mdi:check" width={14} height={14} className="shrink-0" aria-hidden="true" />}
                          <span>{option.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  {touched.spacing && errors.spacing && (
                    <p className="mt-2 text-xs font-medium text-red-400">
                      {errors.spacing}
                    </p>
                  )}
                </div>
              </div>
              <div className="mb-6">
                <Checkbox
                  name="isContinuationOrder"
                  checked={isContinuationOrder}
                  onChange={handleContinuationOrderChange}
                  value="yes"
                  label="This is a continuation of a previous order"
                  className="mt-1"
                />
                {isContinuationOrder && (
                  <div className="mt-3">
                    <FormInput
                      label="Previous Order ID"
                      id="previousOrderId"
                      name="previousOrderId"
                      type="text"
                      value={formData.previousOrderId}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      error={errors.previousOrderId}
                      placeholder="E.g. ID1002"
                      containerStyle="mb-0"
                      isRequired={false}
                    />
                  </div>
                )}
              </div>
              <div/>

              {/* Third Row: Pages and Slides Section */}
              <div className="sm:col-span-1">
                <div className="flex flex-col items-start">
                  {/* Render this only if typeOfPaper is "Powerpoint" */}
                  {formData.typeOfPaper === "Powerpoint" ? (
                    <>
                      {/* Slides Count Section */}
                      <div className="flex flex-col items-start mb-4">
                        <div className="flex flex-row items-start">
                          <label className="font-medium text-sm sm:text-base text-secondary mb-2">
                            Slides <span className="text-light-red">*</span>
                          </label>
                          {/* <div className="relative group cursor-pointer ml-3">
                              <Icon
                                icon="mdi:help-circle-outline"
                                className="text-[#6B7280]"
                                width={18}
                                height={18}
                              />
                              <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm 
                              rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 
                              min-w-[300px] break-words text-center whitespace-normal">
                                {tooltips.PRESENTATION}
                              </div>
                          </div> */}
                        </div>
                        {/* Wrap the button group and text in a flex container */}
                        <div className="flex items-center space-x-[36px]">
                          <div className="flex items-center bg-soft-gray rounded-lg px-4 py-2 space-x-4 w-[127px] justify-between">
                            <button onClick={decrementSlidesCount}>
                              <HiOutlineMinus className="h-5 w-5 text-black" />
                            </button>
                            <span className="font-semibold text-base">
                              {slidescount}
                            </span>
                            <button
                              onClick={incrementSlidesCount}
                              className="h-5 w-5 text-black"
                            >
                              <BsPlusLg />
                            </button>
                          </div>
                          {/* Add text next to the button group */}
                          <span className="text-sm sm:text-base text-black font-medium">
                            {formatSlideCount(slidescount)}
                          </span>
                        </div>
                      </div>

                      {/* Checkbox for Pages */}
                      <Checkbox
                        name="needDocumentation"
                        checked={formData.needDocumentation}
                        onChange={handleCheckboxChange}
                        value="agree"
                        label="I want documentation as well."
                        containerStyle=""
                      />

                      {/* Pages Count Section */}
                      {formData.needDocumentation && (
                        <div className="flex flex-col items-start mt-6">
                          <label className="font-medium text-sm sm:text-base text-secondary mb-2">
                            Pages <span className="text-light-red">*</span>
                          </label>
                          {/* Wrap the button group and text in a flex container */}
                          <div className="flex items-center space-x-[36px]">
                            <div className="flex items-center bg-soft-gray rounded-lg px-4 py-2 space-x-4 w-[127px] justify-between">
                              <button onClick={decrement}>
                                <HiOutlineMinus className="h-5 w-5 text-black" />
                              </button>
                              <span className="font-semibold text-base">
                                {count}
                              </span>
                              <button
                                onClick={increment}
                                className="h-5 w-5 text-black"
                              >
                                <BsPlusLg />
                              </button>
                            </div>
                            {/* Add text next to the button group */}
                            <span className="text-sm sm:text-base font-inter text-black font-medium">
                              {(WORDS_PER_PAGE[formData.spacing] ?? 0) * count} words
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Pages Count Section */}
                      <div className="flex flex-col items-start mb-4 ">
                        <label className="font-medium text-sm sm:text-base text-secondary mb-2">
                          Pages <span className="text-light-red">*</span>
                        </label>
                        {/* Wrap the button group and text in a flex container */}
                        <div className="flex items-center space-x-[36px]">
                          <div className="flex items-center bg-soft-gray rounded-lg px-4 py-2 space-x-4 w-[127px] justify-between">
                            <button onClick={decrement}>
                              <HiOutlineMinus className="h-5 w-5 text-black" />
                            </button>
                            <span className="font-semibold text-base">
                              {count}
                            </span>
                            <button
                              onClick={increment}
                              className="h-5 w-5 text-black"
                            >
                              <BsPlusLg />
                            </button>
                          </div>
                          {/* Add text next to the button group */}
                          <span className="text-sm sm:text-base font-inter text-black font-medium">
                            {(WORDS_PER_PAGE[formData.spacing] ?? 0) * count} words
                          </span>
                        </div>
                      </div>

                      {/* Checkbox for Slides */}
                      {count > 1 && (
                        <div className="flex items-center">
                          <Checkbox
                            name="needPresentation"
                            checked={formData.needPresentation}
                            onChange={handleCheckboxChange}
                            value="agree"
                            label="Request presentation slides"
                            containerStyle=""
                          />
                          <div className="relative group cursor-pointer ml-3">
                            <Icon
                              icon="mdi:help-circle-outline"
                              className="text-[#6B7280]"
                              width={18}
                              height={18}
                            />
                            <div className="absolute z-20 hidden group-hover:block bg-black text-white text-sm 
                              rounded-md px-2 py-1 top-full mt-1 left-1/2 -translate-x-1/2 
                              min-w-[300px] break-words text-center whitespace-normal">
                              {tooltips.PRESENTATION}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Slides Count Section */}
                      {count > 1 && formData.needPresentation && (
                        <div className="flex flex-col items-start mt-6">
                          <label className="font-medium text-sm sm:text-base text-secondary mb-2">
                            Slides <span className="text-light-red">*</span>
                          </label>
                          {/* Wrap the button group and text in a flex container */}
                          <div className="flex items-center space-x-[36px]">
                            <div className="flex items-center bg-soft-gray rounded-lg px-4 py-2 space-x-4 w-[127px] justify-between">
                              <button onClick={decrementSlidesCount}>
                                <HiOutlineMinus className="h-5 w-5 text-black" />
                              </button>
                              <span className="font-semibold text-base">
                                {slidescount}
                              </span>
                              <button
                                onClick={incrementSlidesCount}
                                className="h-5 w-5 text-black"
                              >
                                <BsPlusLg />
                              </button>
                            </div>
                            {/* Add text next to the button group */}
                            <span className="text-sm sm:text-base text-black font-medium">
                              {formatSlideCount(slidescount)}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Checkbox Section */}
            {/* <div className="flex flex-col justify-start w-full mt-6">
              <Checkbox
                checked={formData.removeAiContent}
                onChange={handleCheckboxChange}
                value="agree"
                label="Just remove AI content from my existing paper."
                containerStyle=""
                name="removeAiContent"
              />
              {errors["removeAiContent"] && (
                <p className="ml-11 text-red-400">
                  {errors["removeAiContent"]}
                </p>
              )}
            </div> */}

            {/* Total Cost */}


            <div className="my-10 flex w-full justify-center">
              <div className="w-full max-w-4xl rounded-[22px] border border-[#dfe7ff] bg-[#f8fbff]/80 px-5 py-4">
                <div className="flex flex-col">
                  <div className="flex flex-col gap-4 px-1 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="mt-1 text-[18px] font-semibold leading-7 text-[#1f2a5a]">
                        Total Cost (USD)
                      </h3>
                      {seasonalDiscount.enabled && (
                        <p className="mt-1 text-[13px] font-semibold leading-5 text-blue-shades">
                          {getDiscountDisplayName(priceDetails.discount?.name, seasonalDiscount.name)}: {priceDetails.discount?.percent || seasonalDiscount.percent}% discount has been applied automatically.
                        </p>
                      )}
                    </div>
                    <div className="rounded-[18px] border border-[#c9d8ff] bg-[#eef4ff] px-5 py-3 text-center">
                      {seasonalDiscount.enabled && priceDetails.discount?.amount > 0 && (
                        <span className="block text-[15px] font-medium leading-5 text-blue-shades/70 line-through">
                          {currencyFormatter.format(priceDetails.totalCost || 0)}
                        </span>
                      )}
                      <span className="mt-1 block text-[28px] font-semibold leading-8 text-[#062c55]">
                        {currencyFormatter.format(priceDetails.discountedTotalCost || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Button */}
            <div className="flex justify-between gap-1.5 mt-6 w-full">
              <CustomButton
                text="Back"
                onClick={() => { }}
                width="clamp(76px, 24vw, 104px)"
                height="44px"
                iconDirection="left"
                iconPosition="before"
                disabled={true}
                buttonClassName="gap-2 px-2 text-[12px] sm:px-3"
                iconClassName="h-6 w-6 shrink-0"
              />

              <CustomButton
                text={
                  <>
                    <span className="sm:hidden">Next: Instructions</span>
                    <span className="hidden sm:inline">Next: Paper Instructions</span>
                  </>
                }
                disabled={isLoading || !!errors.previousOrderId || isLoading}
                onClick={handleNextClick}
                width="clamp(128px, 46vw, 190px)"
                height="44px"
                iconDirection="right"
                iconPosition="after"
                buttonClassName="gap-1 px-1.5 text-[11px] leading-none hover:bg-black disabled:hover:bg-slate-300 sm:gap-2 sm:px-3 sm:text-[12px]"
                iconClassName="h-3 w-3 sm:h-4 sm:w-4"
              />
            </div>

            <div className="w-full flex justify-center mt-5">
              <OrderInclusionsStrip />
            </div>

          </div>
          <OrderSummarySidebar
            formData={formData}
            pages={summaryPages}
            slides={summarySlides}
            totalUnits={summaryTotalUnits}
          />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculation;
